import { useForm } from 'react-hook-form';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../../services/alert.service';
import { STORAGE } from '../../../constants';
import { LocalStorageService } from '../../../services/local-storage.service';
import { UsersService } from '../../../services/users.service';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/forms/input.component';
import Button from '../../../components/forms/button.component';
import Icon from '../../../components/forms/icon.component';
import useInputValidation from '../../../hooks/use-input-validation.hook';
import LogData from '../../../components/logs/log-data.component';
import { t } from 'i18next';

interface FormValues {
  user: string;
  password: string;
  new_password: string;
  confirmPassword: string;
}

declare const KTComponents: any;

const ProgressItemProfile = ({ progress, index, labelKey, labelNumber = 1 }: any) => {
  const statusClass = progress[index] ? 'primary' : 'danger';
  const textClass = progress[index] ? 'primary' : 'dark';

  return (
    <div className="d-flex align-items-center">
      <div className={`bullet bullet-dot h-7px w-7px mb- rounded- bg-${statusClass}`} />
      <div className={`ms-2 text-${textClass} fw-bolder`}>{labelNumber}</div>
      <div className={`ms-2 text-${textClass} fw-bolder flex-shrink-0`}>{t(labelKey)}</div>
    </div>
  );
};

function ChangePasswordProfile() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => LocalStorageService.get(STORAGE.SESSION));
  const { t } = useTranslation();
  const { passwordRegex } = useInputValidation();
  const [progress, setProgress] = useState([false, false, false, false, false]);
  const defaultValues: FormValues = {
    user: session._id,
    password: '',
    new_password: '',
    confirmPassword: ''
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  const consecutiveTrueCount = progress.reduce((count, value) => {
    if (value) {
      count += 1;
    } else {
      return count;
    }
    return count;
  }, 0);

  const passwordValue = watch('new_password') || '';
  const passwordPattern = new RegExp(`^${passwordValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);

  const validatePassword = (password: any) => {
    const lengthCriteria = password.length == 10;
    const lowercaseCriteria = /[a-z]/.test(password);
    const uppercaseCriteria = /[A-Z]/.test(password);
    const numberCriteria = /[0-9]/.test(password);
    const specialCriteria = /[!@#$%¡¿/&*.()_+{}<>?]/.test(password);

    setProgress([
      specialCriteria,
      numberCriteria,
      uppercaseCriteria,
      lowercaseCriteria,
      lengthCriteria
    ]);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    validatePassword(password);
    setValue('new_password', password, { shouldValidate: true });
    trigger('new_password');
    reset({
      ...defaultValues,
      user: watch('user'),
      password: watch('password'),
      new_password: watch('new_password'),
      confirmPassword: ''
    });
  };

  const generatePassword = () => {
    const length = 10;
    const lowercase = 'abcdefghijkmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%¡¿/&*()_+{}<>?';

    const allChars = lowercase + uppercase + numbers + special;
    let newPassword = '';

    newPassword += lowercase[Math.floor(Math.random() * lowercase.length)];
    newPassword += uppercase[Math.floor(Math.random() * uppercase.length)];
    newPassword += numbers[Math.floor(Math.random() * numbers.length)];
    newPassword += special[Math.floor(Math.random() * special.length)];

    for (let i = 4; i < length; i++) {
      newPassword += allChars[Math.floor(Math.random() * allChars.length)];
    }

    newPassword = newPassword
      .split('')
      .sort(() => 0.5 - Math.random())
      .join('');

    const typeText = (text: string, index = 0) => {
      if (index < text.length) {
        setValue('new_password', text.slice(0, index + 1), {
          shouldValidate: true
        });
        setTimeout(() => typeText(text, index + 1), 50);
      } else {
        trigger('new_password');
      }
    };
    typeText(newPassword);
    reset({
      ...defaultValues,
      user: watch('user'),
      password: watch('password'),
      new_password: watch('new_password'),
      confirmPassword: ''
    });
  };

  const changePassword = async (data: FormValues, buttonId: string) => {
    const isConfirmed = await AlertService.warning(
      t('profile-settings.tab-one.q-2'),
      t('profile-settings.tab-one.des-2'),
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...rest } = data;
      await UsersService.recoverPassword(rest);
      AlertService.toastSuccess(t('profile-settings.tab-one.a-3'));
      LogData(rest, buttonId, window.location.href);
      reset(defaultValues);
      LocalStorageService.clear();
      navigate('/auth/login');
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE.SESSION) {
        const newSession = LocalStorageService.get(STORAGE.SESSION);
        setSession(newSession);

        if (!newSession) {
          navigate('/auth/login');
          AlertService.toastWarning(t('general.session-expired'));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  useEffect(() => {
    const new_password = watch((value) => {
      validatePassword(value.new_password);
    });
    return () => new_password.unsubscribe();
  }, [watch]);

  KTComponents.init();

  return (
    <>
      <form onSubmit={handleSubmit((data) => changePassword(data, 'updatePassword-8534728399'))}>
        <div className="fv-row mb-3">
          <Input
            label={t('profile-settings.tab-one.currentPassword-input')}
            requiredIndicator="required"
            type={'password'}
            placeholder={t('profile-settings.tab-one.currentPassword-pl')}
            errors={errors?.password}
            {...register('password', {
              required: true,
              setValueAs: (value) => value.trim()
            })}
            onChange={(e: any) => {
              const noSpaces = e.target.value.replace(/\s/g, '');
              setValue('password', noSpaces, { shouldValidate: true });
            }}
            onPaste={(e: any) => {
              const pasted = e.clipboardData.getData('text');
              if (/\s/.test(pasted)) {
                AlertService.toastInfo(
                  '¡ Atención !, no se puede copiar tu contraseña, estas tratando de copiar una contraseña con espacios.'
                );
                e.preventDefault();
              }
            }}
          />
          <div className="mb-5 fw-bolder text-info">
            Ingresa la contraseña con la que iniciaste sesión en ChatBridge.
          </div>
        </div>
        <div className="fv-row mb-3">
          <Input
            label={t('profile-settings.tab-one.newPassword-input')}
            type={'password'}
            requiredIndicator={'required'}
            placeholder={t('profile-settings.tab-one.newPassword-pl')}
            errors={errors.new_password}
            {...register('new_password', {
              required: true,
              setValueAs: (value) => value.trim(),
              pattern: {
                value: passwordRegex,
                message: t('verify-Password.accepted.error-invalid')
              }
            })}
            onChange={(e: any) => {
              const noSpaces = e.target.value.replace(/\s/g, '');
              setValue('new_password', noSpaces, { shouldValidate: true });
              handleInputChange(e);
            }}
            onPaste={(e: any) => {
              const pasted = e.clipboardData.getData('text');
              if (/\s/.test(pasted)) {
                AlertService.toastInfo(
                  '¡ Atención !, no se puede copiar tu contraseña, estas tratando de copiar una contraseña con espacios.'
                );
                e.preventDefault();
              }
            }}
          />
          <div className="d-flex align-items-center mt-1 mb-1">
            {progress.map((isActive, index) => (
              <div
                key={index}
                className={`flex-grow-1 bg-secondary ${
                  index < consecutiveTrueCount ? 'bg-active-primary' : ''
                } ${index < consecutiveTrueCount ? 'active' : ''} rounded h-5px ${
                  index < progress.length - 1 ? 'me-2' : ''
                }`}
              />
            ))}
          </div>
          <div className="mb-5 flex-shrink-0">{t('verify-Password.accepted.txt-config1')}</div>
          <ProgressItemProfile
            progress={progress}
            index={4}
            labelKey="verify-Password.accepted.txt-config2"
            labelNumber={10}
          />
          <ProgressItemProfile
            progress={progress}
            index={3}
            labelKey="verify-Password.accepted.txt-config3"
          />
          <ProgressItemProfile
            progress={progress}
            index={2}
            labelKey="verify-Password.accepted.txt-config4"
          />
          <ProgressItemProfile
            progress={progress}
            index={1}
            labelKey="verify-Password.accepted.txt-config5"
          />
          <ProgressItemProfile
            progress={progress}
            index={0}
            labelKey="verify-Password.accepted.txt-config6"
          />

          <div className="my-5 flex-shrink-0">
            {t('verify-Password.accepted.txt-config7')}{' '}
            <button
              type="button"
              className="btn text-info fw-bolder cursor-pointer mb-1"
              onClick={() => generatePassword()}
            >
              {t('verify-Password.accepted.txt-config8')}
            </button>
          </div>
        </div>

        <div className="fv-row mb-11">
          <Input
            label={t('profile-settings.tab-one.confirmPassword-input')}
            requiredIndicator={'required'}
            type={'password'}
            placeholder={t('profile-settings.tab-one.confirmPassword-pl')}
            errors={errors.confirmPassword}
            {...register('confirmPassword', {
              required: true,
              setValueAs: (value) => value.trim(),
              pattern: {
                value: passwordPattern,
                message: t('verify-Password.accepted.error-match')
              }
            })}
            onChange={(e: any) => {
              const noSpaces = e.target.value.replace(/\s/g, '');
              setValue('confirmPassword', noSpaces, { shouldValidate: true });
            }}
            onPaste={(e: any) => {
              const pasted = e.clipboardData.getData('text');
              if (/\s/.test(pasted)) {
                AlertService.toastInfo(
                  '¡ Atención !, no se puede copiar tu contraseña, estas tratando de copiar una contraseña con espacios.'
                );
                e.preventDefault();
              }
            }}
          />
        </div>

        <div className="card-footer border-3 d-flex justify-content-end">
          <Button
            type="submit"
            className="btn btn-primary hover-scale fw-bolder"
            disabled={!isValid}
            isLoading={isSubmitting}
          >
            {t('profile-settings.tab-one.text-submit')}
            <Icon icon="ki-key" size="fs-1" className="ms-2" />
          </Button>
        </div>
      </form>
    </>
  );
}

export default ChangePasswordProfile;
